import React from 'react';
import { Users, Shield, Eye, Edit3 } from 'lucide-react';

export default function TeamViewMockup({ size = 'sm' }) {
  const isLg = size === 'lg';

  const members = [
    {
      initials: 'AB',
      name: 'Alex B.',
      role: 'Owner',
      color: 'from-blue-500 to-cyan-500',
      roleColor: 'bg-violet-500/15 text-violet-400',
    },
    {
      initials: 'MT',
      name: 'Marcus T.',
      role: 'Admin',
      color: 'from-violet-500 to-purple-500',
      roleColor: 'bg-blue-500/15 text-blue-400',
    },
    {
      initials: 'RK',
      name: 'Rachel K.',
      role: 'Member',
      color: 'from-emerald-500 to-teal-500',
      roleColor: 'bg-emerald-500/15 text-emerald-400',
    },
    {
      initials: 'DL',
      name: 'David L.',
      role: 'Contractor',
      color: 'from-amber-500 to-orange-500',
      roleColor: 'bg-amber-500/15 text-amber-400',
    },
  ];

  const sharedProjects = [
    { address: '1428 Elm St', members: ['AB', 'MT', 'RK'], access: 'Full Access' },
    { address: '742 Evergreen Terrace', members: ['AB', 'DL'], access: 'View Only' },
  ];

  return (
    <div className={`w-full ${isLg ? 'p-4' : 'p-3'} bg-slate-800/80 rounded-xl`}>
      {/* Mini header */}
      <div className="flex items-center gap-2 mb-3">
        <div className="w-5 h-5 rounded bg-violet-500/20 flex items-center justify-center">
          <Users size={11} className="text-violet-400" />
        </div>
        <span className={`${isLg ? 'text-xs' : 'text-[10px]'} font-semibold text-white`}>Team</span>
        {isLg && (
          <span className="ml-auto text-[9px] bg-violet-500/15 text-violet-300 px-2 py-0.5 rounded-full font-medium">
            4 members
          </span>
        )}
      </div>

      {/* Team members */}
      <div className={`space-y-${isLg ? '2' : '1.5'}`}>
        {members.slice(0, isLg ? 4 : 3).map((m, i) => (
          <div
            key={i}
            className={`flex items-center gap-2 bg-slate-700/40 rounded-lg ${isLg ? 'px-3 py-2' : 'px-2 py-1.5'}`}
          >
            <div
              className={`${isLg ? 'w-7 h-7' : 'w-5 h-5'} rounded-full bg-gradient-to-br ${m.color} flex items-center justify-center flex-shrink-0`}
            >
              <span className={`${isLg ? 'text-[9px]' : 'text-[7px]'} font-bold text-white`}>
                {m.initials}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p
                className={`${isLg ? 'text-[11px]' : 'text-[9px]'} font-semibold text-white truncate`}
              >
                {m.name}
              </p>
              {isLg && <p className="text-[8px] text-slate-500">Active now</p>}
            </div>
            <span className={`text-[7px] font-semibold px-1.5 py-0.5 rounded-full ${m.roleColor}`}>
              {m.role}
            </span>
          </div>
        ))}
        {!isLg && (
          <div className="flex items-center justify-center">
            <span className="text-[8px] text-slate-500">+1 more member</span>
          </div>
        )}
      </div>

      {/* Shared projects (lg only) */}
      {isLg && (
        <div className="mt-3">
          <p className="text-[9px] text-slate-500 uppercase tracking-wider font-semibold mb-2">
            Shared Projects
          </p>
          <div className="space-y-1.5">
            {sharedProjects.map((proj, i) => (
              <div
                key={i}
                className="bg-slate-700/30 rounded-lg px-3 py-2 flex items-center justify-between"
              >
                <div>
                  <p className="text-[10px] font-semibold text-white">{proj.address}</p>
                  <div className="flex items-center gap-1 mt-1">
                    {proj.members.map((init, j) => (
                      <div
                        key={j}
                        className="w-4 h-4 rounded-full bg-slate-600 flex items-center justify-center -ml-1 first:ml-0 border border-slate-800"
                      >
                        <span className="text-[6px] font-bold text-slate-300">{init}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {proj.access === 'Full Access' ? (
                    <Edit3 size={9} className="text-emerald-400" />
                  ) : (
                    <Eye size={9} className="text-slate-500" />
                  )}
                  <span
                    className={`text-[8px] font-medium ${proj.access === 'Full Access' ? 'text-emerald-400' : 'text-slate-500'}`}
                  >
                    {proj.access}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Invite badge */}
      {isLg && (
        <div className="mt-3 flex items-center gap-2 bg-blue-500/10 border border-blue-500/20 rounded-lg px-3 py-2">
          <Shield size={11} className="text-blue-400 flex-shrink-0" />
          <p className="text-[9px] text-blue-300">
            Role-based permissions ensure everyone sees only what they need
          </p>
        </div>
      )}
    </div>
  );
}
